/**
 * Billing API Service for V2 Billing System
 *
 * This service handles all communication with the V2 billing backend endpoints.
 * It provides methods for subscription management, addon updates, and pending changes.
 */

import { send_request } from '../../../utils/Request';

// Backend enum mappings
export const BACKEND_PLAN_MAPPING = {
  Free: 'FREE_TRIAL',
  Professional: 'BASE_PLAN',
};

export const FRONTEND_PLAN_MAPPING = {
  FREE_TRIAL: 'Free',
  BASE_PLAN: 'Professional',
};

export const ADDON_TYPE_MAPPING = {
  actions: 'ADDITIONAL_ACTIONS',
  aiCredits: 'ADDITIONAL_AI_CREDITS',
};

export const ADDON_TYPE_REVERSE_MAPPING = {
  ADDITIONAL_ACTIONS: 'actions',
  ADDITIONAL_AI_CREDITS: 'aiCredits',
};

/**
 * Get current subscription details
 */
export const getCurrentSubscription = async () => {
  try {
    const response = await send_request('authz-service/v2/billing/subscription', null, null, 'GET');

    if (response.status === 200 || response.status === 204) {
      return transformBackendSubscriptionToFrontend(response.data);
    }
    throw new Error(`Failed to get subscription: ${response.status}`);
  } catch (error) {
    console.error('Error fetching subscription:', error);

    // If user doesn't have a V2 subscription yet (existing user), create a FREE trial
    if (error.message && error.message.includes('No V2 subscription found')) {
      console.log('No V2 subscription found, creating default FREE trial...');
      try {
        const createResponse = await send_request(
          'authz-service/v2/billing/subscription',
          { plan: 'FREE_TRIAL' },
          null,
          'POST'
        );

        if (createResponse.status === 200 || createResponse.status === 204) {
          return transformBackendSubscriptionToFrontend(createResponse.data);
        }
      } catch (createError) {
        console.error('Failed to create default subscription:', createError);
      }
    }

    throw error;
  }
};

/**
 * Create checkout session for subscription upgrade/plan change
 * Returns a checkout session URL for redirect
 */
export const createCheckoutSession = async (plan, addons = [], successUrl, cancelUrl) => {
  try {
    const backendAddons = addons.map(addon => ({
      addonType: ADDON_TYPE_MAPPING[addon.type],
      quantity: addon.quantity,
    }));

    const requestBody = {
      plan: BACKEND_PLAN_MAPPING[plan],
      addons: backendAddons,
      successUrl,
      cancelUrl,
    };

    const response = await send_request(
      'authz-service/v2/billing/checkout',
      requestBody,
      null,
      'POST'
    );

    if (response.status === 200 || response.status === 204) {
      return response.data; // Should contain { sessionId, url }
    }
    throw new Error(`Failed to create checkout session: ${response.status}`);
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Create checkout session for addon updates
 */
export const createAddonCheckoutSession = async (addonUpdates, successUrl, cancelUrl) => {
  try {
    const backendAddonUpdates = addonUpdates.map(update => ({
      addonType: ADDON_TYPE_MAPPING[update.type],
      newQuantity: update.quantity,
    }));

    const requestBody = {
      addonUpdates: backendAddonUpdates,
      successUrl,
      cancelUrl,
    };

    const response = await send_request(
      'authz-service/v2/billing/checkout/addons',
      requestBody,
      null,
      'POST'
    );

    if (response.status === 200 || response.status === 204) {
      return response.data; // Should contain { sessionId, url }
    }
    throw new Error(`Failed to create addon checkout session: ${response.status}`);
  } catch (error) {
    console.error('Error creating addon checkout session:', error);
    throw error;
  }
};

/**
 * Create a new subscription (for new users) - DEPRECATED, use createCheckoutSession instead
 * @deprecated Use createCheckoutSession for secure checkout flow
 */
export const createSubscription = async (plan, paymentMethodId = null, addons = []) => {
  console.warn(
    'createSubscription is deprecated. Use createCheckoutSession for secure payment processing.'
  );
  try {
    const backendAddons = addons.map(addon => ({
      addonType: ADDON_TYPE_MAPPING[addon.type],
      quantity: addon.quantity,
    }));

    const requestBody = {
      plan: BACKEND_PLAN_MAPPING[plan],
      paymentMethodId,
      addons: backendAddons,
    };

    const response = await send_request('v2/billing/subscription', requestBody, null, 'POST');

    if (response.status === 200 || response.status === 204) {
      return transformBackendSubscriptionToFrontend(response.data);
    }
    throw new Error(`Failed to create subscription: ${response.status}`);
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
};

/**
 * Update addon quantity (for downgrades that don't require payment)
 */
export const updateAddon = async (addonType, newQuantity, applyImmediately = true) => {
  try {
    const requestBody = {
      addonType: ADDON_TYPE_MAPPING[addonType],
      newQuantity,
      applyImmediately,
    };

    const response = await send_request('authz-service/v2/billing/addon', requestBody, null, 'PUT');

    if (response.status === 200 || response.status === 204) {
      return transformBackendSubscriptionToFrontend(response.data);
    }
    throw new Error(`Failed to update addon: ${response.status}`);
  } catch (error) {
    console.error('Error updating addon:', error);
    throw error;
  }
};

/**
 * Change subscription plan - DEPRECATED for upgrades, use createCheckoutSession instead
 * Still used for downgrades that don't require payment
 * @deprecated For upgrades, use createCheckoutSession for secure payment processing
 */
export const changePlan = async (newPlan, paymentMethodId = null) => {
  try {
    console.log('changePlan called with:', { newPlan, paymentMethodId });

    const requestBody = {
      newPlan: BACKEND_PLAN_MAPPING[newPlan],
      paymentMethodId,
    };

    console.log('Request body:', requestBody);

    const response = await send_request(
      'authz-service/v2/billing/subscription/plan',
      requestBody,
      null,
      'PUT'
    );

    console.log('changePlan response:', response);

    if (response.status === 200 || response.status === 204) {
      return transformBackendSubscriptionToFrontend(response.data);
    }
    throw new Error(`Failed to change plan: ${response.status}`);
  } catch (error) {
    console.error('Error changing plan:', error);
    throw error;
  }
};

/**
 * Cancel a pending update
 */
export const cancelPendingUpdate = async pendingUpdateId => {
  try {
    const response = await send_request(
      `authz-service/v2/billing/pending-update/${pendingUpdateId}`,
      null,
      null,
      'DELETE'
    );

    if (response.status === 200 || response.status === 204) {
      return true;
    }
    throw new Error(`Failed to cancel pending update: ${response.status}`);
  } catch (error) {
    console.error('Error canceling pending update:', error);
    throw error;
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async () => {
  try {
    const response = await send_request(
      'authz-service/v2/billing/subscription/cancel',
      null,
      null,
      'POST'
    );

    if (response.status === 200 || response.status === 204) {
      return true;
    }
    throw new Error(`Failed to cancel subscription: ${response.status}`);
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

/**
 * Transform backend subscription data to frontend format
 */
const transformBackendSubscriptionToFrontend = backendData => {
  // Transform addons
  const addOnQuantities = {
    actions: 0,
    aiCredits: 0,
  };

  if (backendData.addons) {
    backendData.addons.forEach(addon => {
      const frontendType = ADDON_TYPE_REVERSE_MAPPING[addon.addonType];
      if (frontendType) {
        addOnQuantities[frontendType] = addon.quantity || 0;
      }
    });
  }

  // Transform pending changes
  let pendingChanges = { plan: null, addOns: null };
  if (backendData.pendingUpdates && backendData.pendingUpdates.length > 0) {
    const planChanges = backendData.pendingUpdates.find(
      update => update.action === 'CANCEL_SUBSCRIPTION'
    );

    if (planChanges) {
      pendingChanges.plan = 'Free'; // Cancellation means going to Free
    }

    const addonChanges = backendData.pendingUpdates.filter(
      update => update.action === 'DOWNGRADE_ADDON' || update.action === 'REMOVE_ADDON'
    );

    if (addonChanges.length > 0) {
      pendingChanges.addOns = {};
      addonChanges.forEach(change => {
        const frontendType = ADDON_TYPE_REVERSE_MAPPING[change.targetAddonType];
        if (frontendType) {
          pendingChanges.addOns[frontendType] = change.newQuantity || 0;
        }
      });
    }
  }

  return {
    currentPlan: FRONTEND_PLAN_MAPPING[backendData.plan] || 'Free',
    addOnQuantities,
    originalQuantities: { ...addOnQuantities },
    pendingChanges,
    showPendingChanges: pendingChanges.plan !== null || pendingChanges.addOns !== null,
    nextBillingDate: backendData.nextBillingDate ? new Date(backendData.nextBillingDate) : null,
    animations: { actions: null, aiCredits: null },
    isEditing: false,
    downgradeDialogOpen: false,
    subscription: backendData, // Keep full backend data for reference
    pendingUpdates: backendData.pendingUpdates || [], // Keep pending updates with IDs for cancellation
  };
};

export default {
  getCurrentSubscription,
  createCheckoutSession,
  createAddonCheckoutSession,
  createSubscription, // Deprecated
  updateAddon,
  changePlan, // Deprecated for upgrades
  cancelPendingUpdate,
  cancelSubscription,
};
